import { supabase } from '../../../shared/lib/supabaseClient';
import { getOwnSubjects } from '../../Subject/services/subjectService';
import { getStudents } from '../../Student/services/studentService';

// Subject ids among `subjects` (each with .name, .grade_level and .subject_sections: [{ section }])
// that are taught to one of `groups` ([{ grade_level, section }]), optionally narrowed to one
// subject name — the same subject (e.g. "English") is often a separate row per grade level, so
// matching by name rather than id covers all of them at once.
export function subjectIdsForGroups(subjects, groups, subjectName = '') {
  const sectionKeys = new Set(groups.map((g) => `${g.grade_level}|${g.section}`));
  return subjects
    .filter((s) => s.subject_sections.some((ss) => sectionKeys.has(`${s.grade_level}|${ss.section}`)))
    .filter((s) => !subjectName || s.name.toLowerCase() === subjectName.toLowerCase())
    .map((s) => s.id);
}

// Per-student present/absent/late/excused counts for `roster`, from attendance_records for
// `subjectIds` between `from` and `to` (inclusive dates).
async function summarize({ roster, subjectIds, from, to }) {
  const counts = {};
  if (subjectIds.length > 0) {
    const { data: records, error } = await supabase
      .from('attendance_records')
      .select('student_id, status')
      .in('subject_id', subjectIds)
      .gte('attendance_date', from)
      .lte('attendance_date', to);
    if (error) throw error;

    records.forEach((r) => {
      const c = (counts[r.student_id] ??= { present: 0, absent: 0, late: 0, excused: 0 });
      c[r.status] += 1;
    });
  }

  return roster.map((student) => ({
    ...student,
    present: 0,
    absent: 0,
    late: 0,
    excused: 0,
    ...counts[student.id],
  }));
}

// `groups` ([{ grade_level, section }]) is limited to the signed-in teacher's own sections.
// `subjectName`, when given, narrows attendance to that subject instead of every subject taught
// to `groups`. attendance_records RLS already limits reads to subjects the teacher owns, so
// filtering to their own subject ids here just narrows it to the sections being viewed.
export async function getAttendanceSummary({ groups, from, to, subjectName = '' }) {
  if (groups.length === 0) return [];

  const [subjects, { data: roster }] = await Promise.all([
    getOwnSubjects(),
    getStudents({ groups, pageSize: 1000 }),
  ]);

  return summarize({ roster, subjectIds: subjectIdsForGroups(subjects, groups, subjectName), from, to });
}

// Same as getAttendanceSummary, but across every subject school-wide rather than one teacher's
// own — for the super-admin view, which attendance_records RLS grants full read access to.
export async function getAttendanceSummaryForAdmin({ groups, from, to, subjectName = '' }) {
  if (groups.length === 0) return [];

  const [{ data: subjects, error }, { data: roster }] = await Promise.all([
    supabase.from('subjects').select('id, name, grade_level, subject_sections(section)'),
    getStudents({ groups, pageSize: 1000 }),
  ]);
  if (error) throw error;

  return summarize({ roster, subjectIds: subjectIdsForGroups(subjects, groups, subjectName), from, to });
}

// Supabase returns at most this many rows per request, so longer histories are read in pages.
const RECORDS_PAGE_SIZE = 1000;

// Every attendance_record of one student for `subjectIds`, across all dates, newest first — the
// student's full history behind one row of the summary.
export async function getStudentAttendanceRecords({ studentId, subjectIds }) {
  if (subjectIds.length === 0) return [];

  const records = [];
  for (let start = 0; ; start += RECORDS_PAGE_SIZE) {
    const { data, error } = await supabase
      .from('attendance_records')
      .select('attendance_date, subject_id, status')
      .eq('student_id', studentId)
      .in('subject_id', subjectIds)
      .order('attendance_date', { ascending: false })
      .order('id', { ascending: true })
      .range(start, start + RECORDS_PAGE_SIZE - 1);
    if (error) throw error;
    records.push(...data);
    if (data.length < RECORDS_PAGE_SIZE) break;
  }
  return records.map((row) => ({ date: row.attendance_date, subjectId: row.subject_id, status: row.status }));
}
